import React, { useEffect, useMemo, useState } from "react";
import {
  BreadcrumbComponent,
  MUITable,
  PaginationComponent,
} from "../../../common/components";
import { routes } from "../../../constants/routes";
import "./hubSpot.scss";
import HubspotFormPreview from "./HubspotFormPreview";
import HubspotActionsDropdown from "./HubspotActionsDropdown";

import EastIcon from "@mui/icons-material/East";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DescriptionIcon from "@mui/icons-material/Description";
import EventIcon from "@mui/icons-material/Event";
import CallIcon from "@mui/icons-material/Call";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Button,
  InputAdornment,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Box,
} from "@mui/material";
import { useLogin } from "../../../common/hooks/useUser";
import {
  getHubSpotForms,
  getHubSpotFormSubmissions,
  disconnectHubSpot,
} from "../../../api/hubspot";
import { useSetLoading } from "../../../common/hooks/appearance";

/** Inline HubSpot sprocket SVG icon */
const HubSpotIcon = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="M18.164 7.931V5.085a2.198 2.198 0 0 0 1.266-1.978V3.04A2.199 2.199 0 0 0 17.238.847h-.067a2.199 2.199 0 0 0-2.193 2.192v.067a2.196 2.196 0 0 0 1.252 1.973l.013.006v2.852a6.22 6.22 0 0 0-2.969 1.31l.012-.009-7.828-6.096a2.497 2.497 0 1 0-1.157 1.515l-.012.006 7.696 5.991a6.176 6.176 0 0 0-1.038 3.446c0 1.343.425 2.588 1.147 3.606l-.013-.019-2.342 2.342a1.968 1.968 0 0 0-.58-.095h-.002a2.033 2.033 0 1 0 2.033 2.033 1.978 1.978 0 0 0-.099-.595l.004.014 2.317-2.317a6.247 6.247 0 1 0 4.782-11.133l-.036-.005zm-.964 9.377a3.206 3.206 0 1 1 3.214-3.206v.002a3.206 3.206 0 0 1-3.206 3.206z" />
  </svg>
);

/** Helper: extract a clean form name without the date suffix */
const getCleanFormName = (name: string): string => {
  // HubSpot auto-appends " (Month Day, Year H:MM:SS AM/PM TZ)" — strip it
  const match = name.match(
    /^(.+?)\s*\((?:January|February|March|April|May|June|July|August|September|October|November|December)\s/i,
  );
  return match ? match[1].trim() : name;
};

/** Helper: format ISO / epoch date into readable string */
const formatDate = (timestamp: any): string => {
  if (!timestamp) return "—";
  const date = new Date(
    typeof timestamp === "number" ? timestamp : Date.parse(timestamp),
  );
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Count total fields across all field groups */
const countFields = (form: any): number => {
  if (!form?.formFieldGroups) return 0;
  return form.formFieldGroups.reduce(
    (sum: number, g: any) => sum + (g.fields?.length || 0),
    0,
  );
};

const HubSpot = () => {
  const REACT_APP_BASEURL =
    process.env.REACT_APP_BASEURL || "http://localhost:3002/api/v1";
  const tenantId = useLogin()?.tenantDetails?.id;
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

  const [searchText, setSearchText] = useState<string>("");
  const [copyToast, setCopyToast] = useState<boolean>(false);
  const [view, setView] = useState<"forms" | "submissions">("forms");
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [submissionsTotal, setSubmissionsTotal] = useState<number>(0);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [submissionsLoading, setSubmissionsLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(5);
  const [pageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = React.useState<number>(1);

  const breadcrumbLink = useMemo(() => {
    const links = [
      { label: "Campaigns", link: "#", handleClick: () => {} },
      { label: "HubSpot", link: "#", handleClick: () => setView("forms") },
    ];
    if (view === "submissions" && selectedForm) {
      links.push({
        label: getCleanFormName(selectedForm?.name || "Submissions"),
        link: "#",
        handleClick: () => {},
      } as any);
    }
    return links;
  }, [view, selectedForm]);

  const connectHubspot = () => {
    window.location.href = `${REACT_APP_BASEURL}/hubspot/auth?tenantId=${tenantId}`;
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      await disconnectHubSpot();
      setIsConnected(false);
      setForms([]);
    } catch (err) {
      console.error("Failed to disconnect HubSpot", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [pageSize, currentPage]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const res = await getHubSpotForms({
        params: { page: currentPage, limit: pageSize },
      });
      setIsConnected(res?.data?.data?.isConnected);
      setForms(res?.data?.data?.forms || []);
      setTotalRecords(res?.data?.data?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };
  const fetchSubmissions = async (
    formGuid: string,
    offset: string | null = null,
  ) => {
    try {
      setSubmissionsLoading(true);
      const res = await getHubSpotFormSubmissions(formGuid, 10, offset);
      const data = res?.data?.data;
      if (data) {
        const submissionList = data.submissions || [];
        setSubmissions(submissionList);
        // Use total from API if available and greater than current list, otherwise use list length
        setSubmissionsTotal(Math.max(data.total || 0, submissionList.length));
        setNextOffset(data.nextOffset || null);
        setHasMore(data?.hasMore || false);
      }
    } catch (err) {
      console.error("Failed to load submissions", err);
    } finally {
      setTimeout(() => {
        setSubmissionsLoading(false);
      }, 500);
    }
  };

  const handleSubmissionsClick = (form: any) => {
    setSelectedForm(form);
    setView("submissions");
    fetchSubmissions(form.guid);
  };

  const shareToast = (form: any) => {
    const shareLink = `https://share.hsforms.com/${form.portalId}/${form.guid}`;
    navigator.clipboard.writeText(shareLink);
    setCopyToast(true);
  };

  /* ── Table configuration (Forms) ── */
  const headers = [
    {
      label: "Form Name",
      key: "formName",
      sortable: true,
    },
    // {
    //   label: "Created By",
    //   key: "createdBy",
    //   sortable: true,
    // },
    {
      label: "Created On",
      key: "createdAt",
      sortable: true,
      fixedColumnWidth: "150px",
    },
    {
      label: "",
      key: "actions",
      sortable: false,
      fixedColumnWidth: "60px",
    },
  ];

  /* ── Table configuration (Submissions) ── */
  const submissionColumns = useMemo(() => {
    if (!submissions || submissions.length === 0) return [];
    return Array.from(
      new Set(submissions.flatMap((sub) => sub.values.map((v: any) => v.name))),
    );
  }, [submissions]);

  const submissionHeaders = useMemo(() => {
    const formatHeaderLabel = (label: string) => {
      // Common HubSpot field overrides
      const overrides: Record<string, string> = {
        firstname: "First Name",
        lastname: "Last Name",
        phonenumber: "Phone Number",
        jobtitle: "Job Title",
      };

      if (overrides[label.toLowerCase()]) return overrides[label.toLowerCase()];

      // Generic: camelCase to words, underscores to spaces, capitalize
      return label
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
        .trim();
    };

    const dynamicHeaders = submissionColumns.map((col) => ({
      label: formatHeaderLabel(col),
      key: col,
      sortable: true,
      fixedColumnWidth: "170px",
    }));

    const submittedAtHeader = {
      label: "Submitted At",
      key: "submittedAt",
      sortable: true,
      fixedColumnWidth: "180px",
    };

    return [...dynamicHeaders, submittedAtHeader];
  }, [submissionColumns]);

  const formattedSubmissions = useMemo(() => {
    return submissions.map((sub, index) => {
      const row: any = {
        submittedAt: new Date(sub.submittedAt).toLocaleString(),
        key: index,
      };
      submissionColumns.forEach((col) => {
        const field = sub.values.find((v: any) => v.name === col);
        row[col] = field ? field.value : "—";
      });
      return row;
    });
  }, [submissions, submissionColumns]);

  const noResultText = useMemo(() => {
    if (loading || submissionsLoading) {
      return "Loading forms...";
    }
    return view === "forms" ? "No forms found" : "No submissions found";
  }, [loading, submissionsLoading, view]);

  const formattedData = useMemo(() => {
    if (!Array.isArray(forms)) return [];

    // Client-side search filter
    const filteredForms = forms.filter((form: any) => {
      const matchName = getCleanFormName(form?.name || "")
        .toLowerCase()
        .includes(searchText.toLowerCase());
      // For createdBy search filtering, if applicable (using placeholder fallback 'Bruce alpha' if undefined)
      const creatorName = "Bruce alpha".toLowerCase(); // Ideally this comes from the API response
      const matchCreator = creatorName.includes(searchText.toLowerCase());

      return matchName || matchCreator;
    });

    return filteredForms.map((form: any) => ({
      formName: getCleanFormName(form?.name || ""),
      // createdBy: "Bruce alpha", // Fallback text if not provided by marketing/v3/forms API
      createdAt: formatDate(form?.createdAt),
      actions: (
        <HubspotActionsDropdown
          row={form}
          onView={(f) => {
            setSelectedForm(f);
            setIsPreviewOpen(true);
          }}
          onShare={shareToast}
          onViewSubmissions={handleSubmissionsClick}
        />
      ),
      key: form?.guid,
    }));
  }, [forms, searchText]);

  const handleRowClick = (rowData: any) => {
    // We disable row click preview to match action pattern, or keep it optional
    const form = forms.find((f: any) => f.guid === rowData.key);
    if (form) {
      setSelectedForm(form);
      setIsPreviewOpen(true);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="hubspot-page-container">
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </div>

      {/* ─── Connection Status Card ─── */}
      <div className="hubspot-cards">
        <div className="hubspot-card">
          <div className="hubspot-card-icon">
            <HubSpotIcon />
          </div>
          <div className="hubspot-card-content">
            <h2>HubSpot Integration</h2>
            <p>Manage forms and capture leads directly from HubSpot</p>
            <div className="hubspot-status">
              <span
                className={`status-dot ${isConnected ? "connected" : "disconnected"}`}
              />
              <span
                className={`status-text ${isConnected ? "connected" : "disconnected"}`}
              >
                {isConnected ? "Connected" : "Not Connected"}
              </span>
            </div>
          </div>
          {isConnected ? (
            <a
              className="navigate-link disconnect-link"
              onClick={handleDisconnect}
              style={{ color: "#fafafa", cursor: "pointer" }}
            >
              Disconnect <EastIcon style={{ fontSize: "14px" }} />
            </a>
          ) : (
            <a className="navigate-link" onClick={connectHubspot}>
              Connect <EastIcon style={{ fontSize: "14px" }} />
            </a>
          )}
        </div>

        {/* ─── HubSpot Documentation Card ─── */}
        <div className="hubspot-card hubspot-docs-card">
          <div className="hubspot-card-icon docs-icon">
            <MenuBookIcon />
          </div>
          <div className="hubspot-card-content">
            <h2>HubSpot Documentation</h2>
            <p>Quick guides for HubSpot essentials</p>
            <div className="hubspot-docs-links">
              <a
                href="https://knowledge.hubspot.com/forms/create-forms"
                target="_blank"
                rel="noopener noreferrer"
                className="doc-link"
              >
                <DescriptionIcon className="link-icon" />
                <span>Forms</span>
                <LaunchIcon className="launch-icon" />
              </a>
              {/* 
              <a
                href="https://knowledge.hubspot.com/meetings-tool/use-meetings"
                target="_blank"
                rel="noopener noreferrer"
                className="doc-link"
              >
                <EventIcon className="link-icon" />
                <span>Meetings</span>
                <LaunchIcon className="launch-icon" />
              </a>
              <a
                href="https://knowledge.hubspot.com/calling/use-the-calling-tool"
                target="_blank"
                rel="noopener noreferrer"
                className="doc-link"
              >
                <CallIcon className="link-icon" />
                <span>Calls</span>
                <LaunchIcon className="launch-icon" />
              </a>
              */}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Forms Toolbar ─── */}
      {isConnected && view === "forms" && (
        <div className="hubspot-forms-toolbar">
          <Typography variant="h3" className="toolbar-heading">
            HubSpot Forms
          </Typography>

          <div className="toolbar-actions">
            <IconButton onClick={fetchForms} className="action-icon-btn">
              <CachedIcon />
            </IconButton>
            <TextField
              placeholder="Search by Form Name,Created By"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  width: "320px",
                  "& fieldset": { borderColor: "#d1d5db" },
                },
              }}
            />
          </div>
        </div>
      )}

      {/* ─── Forms Table ─── */}
      {isConnected && view === "forms" && (
        <>
          <div className="hubspot-forms-list-table">
            <MUITable
              headers={headers}
              data={formattedData}
              noResultsTxt={noResultText}
              onRowClick={handleRowClick}
            />
          </div>
          {totalRecords && totalRecords > 0 ? (
            <div className="hub-spot-forms-pagination-container">
              <PaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          ) : (
            <></>
          )}
        </>
      )}

      {/* ─── Submissions View ─── */}
      {isConnected && view === "submissions" && (
        <>
          <div className="hubspot-forms-toolbar">
            <Typography variant="h3" className="toolbar-heading">
              {getCleanFormName(selectedForm?.name || "")} Submissions
            </Typography>

            <div className="toolbar-actions">
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontWeight: 500 }}
                >
                  Total: {submissionsTotal}
                </Typography>
                <IconButton
                  onClick={() => fetchSubmissions(selectedForm.guid)}
                  className="action-icon-btn"
                >
                  <CachedIcon />
                </IconButton>
              </Box>
            </div>
          </div>
          <>
            {!submissionsLoading && submissions.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 10,
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Typography sx={{ color: "#666", fontSize: "15px" }}>
                  No submissions yet
                </Typography>
              </Box>
            ) : (
              <>
                <div className="hubspot-forms-list-table">
                  <MUITable
                    headers={submissionHeaders}
                    data={formattedSubmissions}
                    noResultsTxt={noResultText}
                  />
                </div>
                {hasMore ? (
                  <div className="submissions-load-more-container">
                    <Button className="load-more-button"
                      onClick={() =>
                        fetchSubmissions(selectedForm.guid, nextOffset)
                      }
                    >
                      Load More
                    </Button>
                  </div>
                ) : (
                  <></>
                )}
              </>
            )}
          </>
        </>
      )}

      <HubspotFormPreview
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        form={selectedForm}
      />

      {/* Copy Link Toast */}
      <Snackbar
        open={copyToast}
        autoHideDuration={2000}
        onClose={() => setCopyToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopyToast(false)}
          sx={{ borderRadius: "6px" }}
        >
          Share link copied to clipboard
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HubSpot;
